/**
 * SCORM 2004 Edit component — upload SCORM packages and preview manifest.
 * Includes title & description inputs (auto-filled from manifest).
 * Independent — no content-core dependency. Supports i18n via `language` prop.
 */

import React, { useCallback } from 'react';
import { ScormUploader } from '../ScormUploader';
import type { ScormUploadResult } from '../types';
import { useScormTranslations } from '../i18n';
import type { Scorm2004Data } from './scorm2004-schema';

export interface Scorm2004EditProps {
    /** Current content data (passed by ContentEditor as value, or ContentDetailsEditor as data) */
    value?: Scorm2004Data;
    data?: Scorm2004Data;
    /** Callback when data changes */
    onChange: (value: Scorm2004Data) => void;
    /** UI language code (e.g. 'en', 'vi'). */
    language?: string;
    /** Whether editing is disabled */
    disabled?: boolean;
    /** Field validation errors */
    errors?: Record<string, string>;
    /** Content ID to associate the SCORM package with */
    contentId?: string;
}

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    fontSize: 14,
    borderRadius: 6,
    border: '1px solid #d1d5db',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
    marginBottom: 4,
};

export function Scorm2004Edit({ value: rawValue, data: rawData, contentId: propContentId, onChange, language, errors }: Scorm2004EditProps) {
    const data = rawValue ?? rawData ?? ({} as Scorm2004Data);
    const contentId = propContentId ?? (data as any)?.id ?? '';
    const t = useScormTranslations(language);

    const handleUploadComplete = useCallback(
        (result: ScormUploadResult) => {
            const manifestTitle =
                (result.manifest as any)?.title ||
                result.original_filename?.replace(/\.zip$/i, '') ||
                '';

            const manifestDesc =
                (result.manifest as any)?.description || '';

            onChange({
                ...data,
                title: data.title || manifestTitle,
                description: data.description || manifestDesc,
                packageId: result.package_id,
                packageFileName: result.original_filename,
                packageFileSize: result.file_size,
                packageUrl: result.package_base_url,
                launchUrl: result.launch_url,
                scormVersion: result.scorm_version,
                manifest: result.manifest as Record<string, unknown>,
            });
        },
        [data, onChange],
    );

    const handleTitleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ ...data, title: e.target.value });
        },
        [data, onChange],
    );

    const handleDescriptionChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange({ ...data, description: e.target.value });
        },
        [data, onChange],
    );

    const hasPackage = !!(data.packageId || data.launchUrl || data.manifest);

    return (
        <div className="scorm-2004-edit" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
            {/* Title */}
            <div>
                <label style={labelStyle}>{t.titleLabel}</label>
                <input
                    type="text"
                    value={data.title || ''}
                    onChange={handleTitleChange}
                    placeholder={t.titlePlaceholder}
                    style={inputStyle}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                />
                {errors?.title && (
                    <p style={{ marginTop: 4, fontSize: 12, color: '#ef4444' }}>{errors.title}</p>
                )}
            </div>

            {/* Description */}
            <div>
                <label style={labelStyle}>{t.descriptionLabel}</label>
                <textarea
                    value={data.description || ''}
                    onChange={handleDescriptionChange}
                    placeholder={t.descriptionPlaceholder}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                />
            </div>

            {/* Package Upload */}
            <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#111827' }}>
                    {t.packageHeading2004}
                </h3>

                {hasPackage && (
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 8,
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}>
                        <span style={{ color: '#16a34a', fontSize: 18 }}>✓</span>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: '#166534' }}>
                                {t.packageUploaded}
                            </div>
                            <div style={{ fontSize: 12, color: '#4ade80' }}>
                                {data.packageFileName}
                                {data.packageFileSize
                                    ? ` · ${(data.packageFileSize / (1024 * 1024)).toFixed(1)} MB`
                                    : ''}
                            </div>
                        </div>
                    </div>
                )}

                <ScormUploader
                    contentId={contentId || undefined}
                    onUploadComplete={handleUploadComplete}
                    onUploadError={(err) => console.error('SCORM upload error:', err)}
                    language={language}
                />

                {errors?.packageId && (
                    <p style={{ marginTop: 4, fontSize: 13, color: '#ef4444' }}>
                        {errors.packageId}
                    </p>
                )}
            </div>
        </div>
    );
}
